import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Endpoint } from '@types';
import { RuntimeConfigService } from 'app/core/config/runtime-config.service';
import type { Observable } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class SharedService {
	private configService = inject(RuntimeConfigService);
	private httpClient = inject(HttpClient);

	get url(): string {
		return this.configService.apiUrl;
	}

	/**
	 * @description Busca entidade do banco pelo ID
	 * @param {(Endpoint | string)} entity Endpoint da entidade
	 * @param {string} id identificador da entidade
	 * @return {*} Observable com a entidade buscada
	 */
	getEntity<T>(entity: Endpoint | string, id: string): Observable<T> {
		return this.httpClient.get<T>(`${this.url}${entity}/${id}`);
	}

	/**
	 * @description Busca entidades do banco
	 * @param {(Endpoint | string)} entity Endpoint da entidade
	 * @param {*} [params] parâmetros adicionais para a requisição
	 * @example params = { page: 1, size: 10 }
	 * @return {*} Observable com a lista de entidades
	 */
	getEntities<T>(
		entity: Endpoint | string,
		params?: HttpParams | { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> },
	): Observable<T[]> {
		return this.httpClient.get<T[]>(`${this.url}${entity}`, { params });
	}

	/**
	 * @description Cria uma nova entidade no banco
	 * @param {(Endpoint | string)} entity Endpoint da entidade
	 * @param {T} data Dados da entidade a ser criada
	 * @return {*} Observable com a entidade criada
	 */
	postEntity<T>(entity: Endpoint | string, data: T): Observable<T> {
		return this.httpClient.post<T>(`${this.url}${entity}`, data);
	}

	/**
	 * @description Atualiza uma entidade existente no banco
	 * @param {(Endpoint | string)} entity Endpoint da entidade
	 * @param {string} id identificador da entidade
	 * @param {Partial<T>} data Dados da entidade a ser atualizada
	 * @return {*} Observable com a entidade atualizada
	 */
	patchEntity<T>(entity: Endpoint | string, id: string | number, data: Partial<T>): Observable<T> {
		return this.httpClient.patch<T>(`${this.url}${entity}/${id}`, data);
	}

	/**
	 * @description Deleta uma entidade do banco
	 * @param {(Endpoint | string)} entity Endpoint da entidade
	 * @param {(string | number)} id identificador da entidade
	 * @return {*}  {Observable<T>} Observable com a confirmação da exclusão
	 */
	deleteEntity<T>(entity: Endpoint | string, id: string | number): Observable<T> {
		return this.httpClient.delete<T>(`${this.url}${entity}/${id}`);
	}
}
